import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChartHelper from './ChartHelper';
import MicrotrendComparisonPanel from './MicrotrendComparisonPanel';
import { recentMicrotrendComparisonWindow } from './microtrendComparisonDisplay';
import ChartPatternPanel from './ChartPatternPanel.tsx';
import Alert from '../UI/Alert';
import ChartControls from './ChartControls';
import ChartToolbar, { type OverlayToggle } from './ChartToolbar.tsx';
import ComparisonSearch from './ComparisonSearch.tsx';
import { useTrpcChartData } from './hooks/useTrpcChartData.ts';
import IndicatorSelector from './IndicatorSelector';
import StrategyEntriesSelector from './StrategyEntriesSelector';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { Button, Modal, Tabs } from '../UI';
import type { Tab } from '../UI/Tabs';
import { useAuth } from '@/context/AuthContext';
import {
    ChartPatternKey,
    type AssetOption,
    type BarDuration,
    type ChartLine,
    type ChartMarker,
    type ChartPatternDisplayMode,
    type ChartPatternKey as ChartPatternKeyType,
    type ChartPatternResolvedEvent,
    type ChartPatternResolvedOccurrence,
    type ChartPatternResolvedSnapshot,
    type ChartPatternSummary,
    type ChartParams,
    type ChartTextItem,
    type ComparisonEntity,
    type EntityTypeUnion,
    type HexColor,
    type HtmlColor,
    type IndicatorInput,
    type PriceLine
} from '@einhaus/project314-types';

import { getLookbackDateForDuration } from '@/utils/dateHelpers/dateHelpers.ts';
import { changeChartDuration, hasExplicitChartDateWindow, resolveChartDateWindow } from './chartDateWindow';
import { assignColorsToIndicator } from '@/utils/chartHelpers/assignColorsToIndicator.ts';
import { generateIndicatorKey } from '@einhaus/project314-shared';
import { nanoid } from 'nanoid';
import { useLocation } from 'react-router-dom';
import { handleTrpcQuery } from '@/api/trpcResponseHandler.ts';
import { isChartPatternDurationSupported } from './chartPatternDisplayHelpers.ts';

const CHART_DURATION_QUERY_VALUES: BarDuration[] = ['1m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'];

const isChartDurationQueryValue = (value: string | null): value is BarDuration => {
    if (!value) return false;
    return CHART_DURATION_QUERY_VALUES.includes(value as BarDuration);
};

const parseBooleanQueryParam = (value: string | null): boolean | undefined => {
    if (value === null) return undefined;
    if (value === '1' || value.toLowerCase() === 'true') return true;
    if (value === '0' || value.toLowerCase() === 'false') return false;
    return undefined;
};

const CHART_PATTERN_DISPLAY_MODES: ChartPatternDisplayMode[] = ['selected', 'allCurrent', 'focused', 'historicalOccurrences', 'pitReplay'];
const CHART_PATTERN_KEYS: ChartPatternKeyType[] = Object.values(ChartPatternKey);
type ChartPatternFocusPaddingPreset = 'default' | 'audit';

const isChartPatternDisplayMode = (value: string | null): value is ChartPatternDisplayMode => {
    if (!value) return false;
    return CHART_PATTERN_DISPLAY_MODES.includes(value as ChartPatternDisplayMode);
};

const isChartPatternKey = (value: string | null): value is ChartPatternKeyType => {
    if (!value) return false;
    return CHART_PATTERN_KEYS.includes(value as ChartPatternKeyType);
};

const parsePositiveIntegerQueryParam = (value: string | null): number | undefined => {
    if (!value) return undefined;

    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed <= 0) {
        return undefined;
    }

    return parsed;
};

interface ChartQueryOverrides {
    chartPatternAsOfDateTime: string | null;
    chartPatternDisplayMode: ChartPatternDisplayMode | null;
    chartPatternKeyFilter: ChartPatternKeyType | null;
    duration: BarDuration | null;
    endDateTime: string | null;
    focusedChartPatternOccurrenceId: number | undefined;
    shouldIncludeChartPatterns: boolean | undefined;
    startDateTime: string | null;
}

const parseChartQueryOverrides = (search: string): ChartQueryOverrides => {
    const queryParams = new URLSearchParams(search);
    const durationFromQuery = queryParams.get('duration');
    const chartPatternModeFromQuery = queryParams.get('chartPatternMode');
    const chartPatternKeyFromQuery = queryParams.get('chartPatternKey');

    return {
        chartPatternAsOfDateTime: queryParams.get('chartPatternAsOfDateTime'),
        chartPatternDisplayMode: isChartPatternDisplayMode(chartPatternModeFromQuery) ? chartPatternModeFromQuery : null,
        chartPatternKeyFilter: isChartPatternKey(chartPatternKeyFromQuery) ? chartPatternKeyFromQuery : null,
        duration: isChartDurationQueryValue(durationFromQuery) ? durationFromQuery : null,
        endDateTime: queryParams.get('endDateTime'),
        focusedChartPatternOccurrenceId: parsePositiveIntegerQueryParam(queryParams.get('chartPatternOccurrenceId')),
        shouldIncludeChartPatterns: parseBooleanQueryParam(queryParams.get('chartPatterns')),
        startDateTime: queryParams.get('startDateTime')
    };
};

const isKeyboardShortcutTargetEditable = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    return target.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName);
};

interface ChartContainerProps {
    chartPatternAsOfDateTime?: string;
    chartPatternFocusPaddingPreset?: ChartPatternFocusPaddingPreset;
    chartPatternKeyFilter?: ChartPatternKeyType | null;
    chartLinesOverride?: ChartLine[];
    consoleItemsOverride?: ChartTextItem[];
    chartPatternOccurrenceIdsOverride?: number[] | null;
    comparisonEntity?: ComparisonEntity | null;
    duration?: BarDuration;
    endDateTime?: string;
    entity: EntityTypeUnion;
    focusedChartPatternOccurrenceId?: number | null;
    hideComparisonSearch?: boolean;
    hideControls?: boolean;
    /** Hides the settings tabs under the chart but keeps the toolbar; focus mode still offers them. */
    hideSettingsPanel?: boolean;
    markersOverride?: ChartMarker[];
    onInitialRenderSettled?: (() => void) | null;
    option?: AssetOption | null;
    indicators?: IndicatorInput[];
    priceLinesOverride?: PriceLine[];
    chartPatternDisplayMode?: ChartPatternDisplayMode;
    shouldRenderChartPatternAnchorMarkers?: boolean;
    shouldRenderChartPatternEventMarkers?: boolean;
    shouldRenderChartPatternStateMarkers?: boolean;
    shouldIncludeChartPatterns?: boolean;
    shouldAutoZoomFocusedChartPattern?: boolean;
    shouldShowChartPatternPanel?: boolean;
    startDateTime?: string;
    shouldIncludeVolumeSeries?: boolean;
}

export interface IndicatorInputWithChartSettings {
    colors: HtmlColor[];
    id: string;
    indicator: IndicatorInput;
}

export interface ChartSettings extends Omit<ChartParams, 'entityId' | 'entityType' | 'indicators'> {
    chartPatternFocusPaddingPreset: ChartPatternFocusPaddingPreset;
    chartPatternOccurrenceIdsOverride?: number[];
    entity: EntityTypeUnion;
    comparisonEntity?: ComparisonEntity | null;
    option?: AssetOption | null;
    shouldAutoRefreshBars?: boolean;
    shouldRemoveAfterHours: boolean;
    indicatorsWithChartSettings: IndicatorInputWithChartSettings[];
    duration: BarDuration;
    hasDateTimeChanged: boolean;
    key: string;
    shouldAutoZoomFocusedChartPattern: boolean;
    shouldRenderChartPatternAnchorMarkers: boolean;
    shouldRenderChartPatternEventMarkers: boolean;
    shouldRenderChartPatternStateMarkers: boolean;
}

const ChartContainer: React.FC<ChartContainerProps> = ({
    chartPatternAsOfDateTime,
    chartPatternFocusPaddingPreset = 'default',
    chartPatternKeyFilter,
    chartLinesOverride,
    consoleItemsOverride,
    chartPatternOccurrenceIdsOverride,
    duration,
    endDateTime,
    entity,
    focusedChartPatternOccurrenceId,
    hideComparisonSearch = false,
    hideControls = false,
    hideSettingsPanel = false,
    indicators,
    markersOverride,
    onInitialRenderSettled = null,
    option,
    priceLinesOverride,
    chartPatternDisplayMode = 'selected',
    shouldRenderChartPatternAnchorMarkers = true,
    shouldRenderChartPatternEventMarkers = true,
    shouldRenderChartPatternStateMarkers = true,
    shouldIncludeChartPatterns,
    shouldAutoZoomFocusedChartPattern = true,
    shouldShowChartPatternPanel = true,
    startDateTime,
    shouldIncludeVolumeSeries
}) => {
    const chartShellRef = useRef<HTMLDivElement>(null);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [chartHelper, setChartHelper] = useState<ChartHelper | null>(null);
    const [chartFlowHeight, setChartFlowHeight] = useState<number | null>(null);
    const [isFocusMode, setIsFocusMode] = useState(false);
    const [microtrendComparison, setMicrotrendComparison] = useState(false);
    const [isFocusSettingsOpen, setIsFocusSettingsOpen] = useState(false);
    const [paramsKey, setParamsKey] = useState('');
    const renderGenerationRef = useRef(0);
    const canUseFocusMode = !hideControls;

    const { isAuthenticated } = useAuth();
    const location = useLocation();

    const usedColors: HexColor[] = [];

    const generateDefaultSettings = (): ChartSettings => ({
        ...(() => {
            const queryOverrides = parseChartQueryOverrides(location.search);
            const resolvedDuration = queryOverrides.duration ?? duration ?? '1d';

            return {
                duration: resolvedDuration,
                ...resolveChartDateWindow(resolvedDuration, { startDateTime, endDateTime }, queryOverrides),
                shouldIncludeChartPatterns: queryOverrides.shouldIncludeChartPatterns ?? shouldIncludeChartPatterns ?? false,
                chartPatternDisplayMode: queryOverrides.chartPatternDisplayMode ?? chartPatternDisplayMode,
                chartPatternKeyFilter: queryOverrides.chartPatternKeyFilter ?? chartPatternKeyFilter ?? undefined,
                chartPatternAsOfDateTime: queryOverrides.chartPatternAsOfDateTime ?? chartPatternAsOfDateTime,
                focusedChartPatternOccurrenceId:
                    queryOverrides.focusedChartPatternOccurrenceId ?? focusedChartPatternOccurrenceId ?? undefined
            };
        })(),
        entity,
        option,
        shouldRemoveAfterHours: entity.entityType !== 'Cryptocurrency',
        indicatorsWithChartSettings: (indicators ?? []).map((indicator) => {
            const colors = assignColorsToIndicator(indicator, usedColors);
            usedColors.push(...colors);
            return { colors, id: `${generateIndicatorKey(indicator)}_${nanoid()}`, indicator };
        }),
        legend: '',
        markers: markersOverride ?? [],
        chartLines: chartLinesOverride ?? [],
        priceLines: priceLinesOverride ?? [],
        priceTrajectories: [],
        shouldAutoRefreshBars: false,
        shouldIncludeCandlestickPatterns: false,
        shouldAutoZoomFocusedChartPattern,
        shouldRenderChartPatternAnchorMarkers,
        shouldRenderChartPatternEventMarkers,
        shouldRenderChartPatternStateMarkers,
        chartPatternFocusPaddingPreset,
        chartPatternOccurrenceIdsOverride: chartPatternOccurrenceIdsOverride ?? undefined,
        shouldIncludeHighsAndLows: false,
        shouldIncludeMicroTrends: false,
        shouldIncludeSupplyDemandZones: false,
        shouldIncludeTrendlines: false,
        shouldIncludeVolumeSeries: shouldIncludeVolumeSeries ?? true,
        tooltipMarkers: [],
        consoleItems: consoleItemsOverride ?? [],
        comparisonEntity: null,
        key: nanoid()
    });

    const [chartSettings, setChartSettings] = useState<ChartSettings>(() => generateDefaultSettings());

    const { bars, isLoading, error, comparisonData, comparisonError } = useTrpcChartData(chartSettings);
    const chartLoadingState = isLoading ? 'loading' : error || comparisonError ? 'error' : bars.length > 0 ? 'ready' : 'empty';

    // ── Chart Pattern Data ──────────────────────────────────────────────

    const shouldFetchChartPatterns =
        chartSettings.shouldIncludeChartPatterns === true &&
        chartSettings.entity.entityType === 'Asset' &&
        isChartPatternDurationSupported(chartSettings.duration) &&
        Boolean(chartSettings.startDateTime) &&
        Boolean(chartSettings.endDateTime);

    const chartPatternsQuery = useAppQuery({
        ...trpc.entity.fetchChartPatterns.queryOptions({
            entityId: chartSettings.entity.id,
            entityType: chartSettings.entity.entityType,
            startDateTime: chartSettings.startDateTime,
            endDateTime: chartSettings.endDateTime,
            duration: chartSettings.duration,
            asOfDateTime: chartSettings.chartPatternAsOfDateTime
        }),
        enabled: shouldFetchChartPatterns
    });

    const chartPatternSummary: ChartPatternSummary | null = chartPatternsQuery.data?.summary ?? null;
    const chartPatternSnapshots: ChartPatternResolvedSnapshot[] = chartPatternsQuery.data?.chartPatterns ?? [];
    const chartPatternSnapshotSeries: ChartPatternResolvedSnapshot[] = chartPatternsQuery.data?.chartPatternSnapshotSeries ?? [];

    const chartPatternHistoricalOccurrencesQuery = useAppQuery({
        ...trpc.entity.fetchChartPatternHistoricalOccurrences.queryOptions({
            entityId: chartSettings.entity.id,
            entityType: chartSettings.entity.entityType,
            startDateTime: chartSettings.startDateTime,
            endDateTime: chartSettings.endDateTime,
            duration: chartSettings.duration,
            asOfDateTime: chartSettings.chartPatternAsOfDateTime
        }),
        enabled: shouldFetchChartPatterns
    });

    const chartPatternHistoricalOccurrences: ChartPatternResolvedOccurrence[] =
        chartPatternHistoricalOccurrencesQuery.data?.occurrences ?? [];

    const chartPatternHistoricalEvents: ChartPatternResolvedEvent[] = chartPatternHistoricalOccurrencesQuery.data?.events ?? [];

    const chartPatternPitReplayQuery = useAppQuery({
        ...trpc.entity.fetchChartPatternPitReplay.queryOptions({
            entityId: chartSettings.entity.id,
            entityType: chartSettings.entity.entityType,
            startDateTime: chartSettings.startDateTime,
            endDateTime: chartSettings.endDateTime,
            duration: chartSettings.duration,
            asOfDateTime: chartSettings.chartPatternAsOfDateTime
        }),
        enabled: shouldFetchChartPatterns
    });

    const chartPatternPitReplaySnapshots: ChartPatternResolvedSnapshot[] = chartPatternPitReplayQuery.data?.snapshots ?? [];
    const chartPatternPitReplayEvents: ChartPatternResolvedEvent[] = chartPatternPitReplayQuery.data?.events ?? [];

    const isChartPatternPanelLoading =
        shouldFetchChartPatterns &&
        (chartPatternsQuery.isLoading || chartPatternHistoricalOccurrencesQuery.isLoading || chartPatternPitReplayQuery.isLoading);

    // ── Query Params ────────────────────────────────────────────────────

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const paramsKey = queryParams.get('paramsKey') || '';
        setParamsKey(paramsKey);
    }, [location]);

    const chartParamsQuery = useAppQuery({
        ...trpc.strategy.fetchChartParams.queryOptions({ paramsKey: paramsKey || '' }),
        enabled: !!paramsKey
    });

    useEffect(() => {
        if (chartParamsQuery.error) {
            handleTrpcQuery(chartParamsQuery, {
                fallbackErrorMessage: 'Failed to load chart parameters',
                showErrorToast: true,
                setError: (msg) => console.error(`Error fetching chart params: ${msg}`)
            });
        } else if (chartParamsQuery.data?.params) {
            setChartSettings((prevChartSettings) => ({
                ...prevChartSettings,
                key: nanoid(),
                ...chartParamsQuery.data.params,
                hasDateTimeChanged: hasExplicitChartDateWindow(chartParamsQuery.data.params ?? {}) || prevChartSettings.hasDateTimeChanged,
                indicatorsWithChartSettings: chartParamsQuery.data.params?.indicators
                    ? chartParamsQuery.data.params.indicators.map((indicator) => {
                          const colors = assignColorsToIndicator(indicator, usedColors);
                          usedColors.push(...colors);
                          return {
                              colors,
                              id: `${generateIndicatorKey(indicator)}_${Date.now()}`,
                              indicator
                          };
                      })
                    : []
            }));
        }
    }, [chartParamsQuery.data, chartParamsQuery.error]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const durationFromQuery = queryParams.get('duration');
        const shouldIncludeChartPatternsFromQuery = parseBooleanQueryParam(queryParams.get('chartPatterns'));
        const chartPatternAsOfDateTimeFromQuery = queryParams.get('chartPatternAsOfDateTime');
        const chartPatternModeFromQuery = queryParams.get('chartPatternMode');
        const chartPatternKeyFromQuery = queryParams.get('chartPatternKey');
        const chartPatternOccurrenceIdFromQuery = parsePositiveIntegerQueryParam(queryParams.get('chartPatternOccurrenceId'));
        const startDateTimeFromQuery = queryParams.get('startDateTime');
        const endDateTimeFromQuery = queryParams.get('endDateTime');
        const paramsKeyFromQuery = queryParams.get('paramsKey') || '';
        const nextDuration = isChartDurationQueryValue(durationFromQuery) ? durationFromQuery : null;
        const nextChartPatternMode = isChartPatternDisplayMode(chartPatternModeFromQuery) ? chartPatternModeFromQuery : null;
        const nextChartPatternKey = isChartPatternKey(chartPatternKeyFromQuery) ? chartPatternKeyFromQuery : null;

        if (
            !nextDuration &&
            shouldIncludeChartPatternsFromQuery === undefined &&
            !chartPatternAsOfDateTimeFromQuery &&
            !nextChartPatternMode &&
            !nextChartPatternKey &&
            chartPatternOccurrenceIdFromQuery === undefined &&
            !startDateTimeFromQuery &&
            !endDateTimeFromQuery
        ) {
            return;
        }

        setChartSettings((prevChartSettings) => {
            const resolvedDuration = nextDuration ?? prevChartSettings.duration;
            const hasQueryDateOverride = Boolean(startDateTimeFromQuery || endDateTimeFromQuery);

            const shouldResetWindowForDurationQuery =
                Boolean(nextDuration) &&
                !hasQueryDateOverride &&
                !prevChartSettings.hasDateTimeChanged &&
                !paramsKeyFromQuery &&
                startDateTime === undefined &&
                endDateTime === undefined;

            return {
                ...prevChartSettings,
                key: nanoid(),
                duration: resolvedDuration,
                shouldIncludeChartPatterns: shouldIncludeChartPatternsFromQuery ?? prevChartSettings.shouldIncludeChartPatterns,
                chartPatternDisplayMode: nextChartPatternMode ?? prevChartSettings.chartPatternDisplayMode,
                chartPatternKeyFilter: nextChartPatternKey ?? prevChartSettings.chartPatternKeyFilter,
                chartPatternAsOfDateTime: chartPatternAsOfDateTimeFromQuery ?? prevChartSettings.chartPatternAsOfDateTime,
                focusedChartPatternOccurrenceId: chartPatternOccurrenceIdFromQuery ?? prevChartSettings.focusedChartPatternOccurrenceId,
                startDateTime:
                    startDateTimeFromQuery ??
                    (shouldResetWindowForDurationQuery ? getLookbackDateForDuration(resolvedDuration) : prevChartSettings.startDateTime),
                endDateTime: endDateTimeFromQuery ?? prevChartSettings.endDateTime,
                hasDateTimeChanged: hasQueryDateOverride || prevChartSettings.hasDateTimeChanged
            };
        });
    }, [entity.entityType, entity.id, location.search, chartParamsQuery.data?.params]);

    // ── Sync with prop changes ──────────────────────────────────────────

    useEffect(() => {
        if (
            entity.id !== chartSettings.entity.id ||
            entity.entityType !== chartSettings.entity.entityType ||
            option?.id !== chartSettings.option?.id ||
            (duration !== undefined && duration !== chartSettings.duration) ||
            (startDateTime !== undefined && startDateTime !== chartSettings.startDateTime) ||
            (endDateTime !== undefined && endDateTime !== chartSettings.endDateTime) ||
            (shouldIncludeChartPatterns !== undefined && shouldIncludeChartPatterns !== chartSettings.shouldIncludeChartPatterns) ||
            (chartPatternAsOfDateTime !== undefined && chartPatternAsOfDateTime !== chartSettings.chartPatternAsOfDateTime) ||
            chartPatternFocusPaddingPreset !== chartSettings.chartPatternFocusPaddingPreset ||
            chartPatternOccurrenceIdsOverride !== chartSettings.chartPatternOccurrenceIdsOverride ||
            (chartPatternKeyFilter ?? undefined) !== chartSettings.chartPatternKeyFilter ||
            chartLinesOverride !== chartSettings.chartLines ||
            consoleItemsOverride !== chartSettings.consoleItems ||
            JSON.stringify(indicators ?? []) !==
                JSON.stringify(chartSettings.indicatorsWithChartSettings.map(({ indicator }) => indicator)) ||
            markersOverride !== chartSettings.markers ||
            priceLinesOverride !== chartSettings.priceLines ||
            shouldAutoZoomFocusedChartPattern !== chartSettings.shouldAutoZoomFocusedChartPattern ||
            shouldRenderChartPatternAnchorMarkers !== chartSettings.shouldRenderChartPatternAnchorMarkers ||
            shouldRenderChartPatternEventMarkers !== chartSettings.shouldRenderChartPatternEventMarkers ||
            shouldRenderChartPatternStateMarkers !== chartSettings.shouldRenderChartPatternStateMarkers ||
            (shouldIncludeVolumeSeries !== undefined && shouldIncludeVolumeSeries !== chartSettings.shouldIncludeVolumeSeries) ||
            chartPatternDisplayMode !== (chartSettings.chartPatternDisplayMode ?? 'selected') ||
            (focusedChartPatternOccurrenceId ?? undefined) !== chartSettings.focusedChartPatternOccurrenceId
        ) {
            setChartSettings(generateDefaultSettings());
        }
    }, [
        chartPatternDisplayMode,
        chartPatternAsOfDateTime,
        chartPatternFocusPaddingPreset,
        chartPatternOccurrenceIdsOverride,
        chartPatternKeyFilter,
        chartLinesOverride,
        consoleItemsOverride,
        duration,
        endDateTime,
        entity,
        focusedChartPatternOccurrenceId,
        indicators,
        option?.id,
        markersOverride,
        priceLinesOverride,
        shouldAutoZoomFocusedChartPattern,
        shouldRenderChartPatternAnchorMarkers,
        shouldRenderChartPatternEventMarkers,
        shouldRenderChartPatternStateMarkers,
        shouldIncludeChartPatterns,
        shouldIncludeVolumeSeries,
        startDateTime
    ]);

    // ── Chart Helper Lifecycle ──────────────────────────────────────────

    useEffect(() => {
        if (chartContainerRef.current?.clientHeight === 0) {
            chartContainerRef.current.style.height = '700px';
        }
    }, []);

    useEffect(() => {
        if (chartHelper || !chartContainerRef.current) return;

        const helper = new ChartHelper(chartContainerRef.current, chartSettings);
        setChartHelper(helper);
    }, [chartSettings.key, chartHelper]);

    useEffect(() => {
        if (chartHelper) chartHelper.updateSettings(chartSettings);
    }, [chartHelper, chartSettings.key]);

    useEffect(() => {
        return () => {
            chartHelper?.destroy();
        };
    }, [chartHelper]);

    useEffect(() => {
        if (!chartHelper) return;

        chartHelper.setChartPatternData({
            snapshots: shouldFetchChartPatterns ? chartPatternSnapshots : [],
            snapshotSeries: shouldFetchChartPatterns ? chartPatternSnapshotSeries : [],
            historicalOccurrences: shouldFetchChartPatterns ? chartPatternHistoricalOccurrences : [],
            historicalEvents: shouldFetchChartPatterns ? chartPatternHistoricalEvents : [],
            pitReplaySnapshots: shouldFetchChartPatterns ? chartPatternPitReplaySnapshots : [],
            pitReplayEvents: shouldFetchChartPatterns ? chartPatternPitReplayEvents : [],
            summary: shouldFetchChartPatterns ? chartPatternSummary : null,
            shouldRenderEventMarkers: chartSettings.shouldRenderChartPatternEventMarkers
        });
    }, [
        chartSettings.shouldRenderChartPatternEventMarkers,
        chartHelper,
        chartPatternHistoricalEvents,
        chartPatternHistoricalOccurrences,
        chartPatternPitReplayEvents,
        chartPatternPitReplaySnapshots,
        chartPatternSnapshots,
        chartPatternSnapshotSeries,
        chartPatternSummary,
        shouldFetchChartPatterns
    ]);

    useEffect(() => {
        if (chartHelper && !isLoading && bars.length > 0) {
            const renderGeneration = ++renderGenerationRef.current;

            void chartHelper.updateBars(bars, comparisonData).then(() => {
                if (renderGenerationRef.current !== renderGeneration) return;
                if (!onInitialRenderSettled) return;

                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        if (renderGenerationRef.current === renderGeneration) {
                            onInitialRenderSettled();
                        }
                    });
                });
            });
        }
    }, [bars, chartHelper, comparisonData, isLoading, onInitialRenderSettled]);

    useEffect(() => {
        if (!chartHelper || isLoading || bars.length > 0 || !onInitialRenderSettled) {
            return;
        }

        const renderGeneration = ++renderGenerationRef.current;

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (renderGenerationRef.current === renderGeneration) {
                    onInitialRenderSettled();
                }
            });
        });
    }, [bars.length, chartHelper, comparisonError, error, isLoading, onInitialRenderSettled]);

    useEffect(() => {
        if (chartHelper && !isLoading && bars.length > 0) {
            const renderGeneration = ++renderGenerationRef.current;

            void chartHelper.updateBars(bars, comparisonData).then(() => {
                if (renderGenerationRef.current !== renderGeneration) return;
                if (!onInitialRenderSettled) return;

                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        if (renderGenerationRef.current === renderGeneration) {
                            onInitialRenderSettled();
                        }
                    });
                });
            });
        }
    }, [
        chartHelper,
        bars,
        comparisonData,
        chartPatternHistoricalEvents,
        chartPatternHistoricalOccurrences,
        chartPatternPitReplayEvents,
        chartPatternPitReplaySnapshots,
        chartPatternsQuery.data?.chartPatterns,
        chartPatternsQuery.data?.summary,
        isLoading,
        chartSettings.key,
        onInitialRenderSettled
    ]);

    // ── Handlers ────────────────────────────────────────────────────────

    const handleAfterHoursToggle = useCallback((includeAfterHours: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldRemoveAfterHours: !includeAfterHours
        }));
    }, []);

    const handleAutoRefreshToggle = useCallback((shouldAutoRefresh: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldAutoRefreshBars: shouldAutoRefresh
        }));
    }, []);

    const handleCandlestickPatternsToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeCandlestickPatterns: show
        }));
    }, []);

    const handleChartPatternsToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: show
        }));
    }, []);

    const handleHighsAndLowsToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeHighsAndLows: show
        }));
    }, []);

    const handleMicroTrendsToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeMicroTrends: show
        }));
    }, []);

    const handleSupplyDemandZonesToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeSupplyDemandZones: show
        }));
    }, []);

    const handleTrendlinesToggle = useCallback((show: boolean) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeTrendlines: show
        }));
    }, []);

    const handleFocusChartPatternOccurrence = useCallback((occurrenceId: number, targetDisplayMode?: ChartPatternDisplayMode) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternDisplayMode:
                targetDisplayMode ??
                (prev.chartPatternDisplayMode === 'historicalOccurrences' || prev.chartPatternDisplayMode === 'pitReplay'
                    ? 'pitReplay'
                    : 'focused'),
            focusedChartPatternOccurrenceId: occurrenceId
        }));
    }, []);

    const handleShowSelectedChartPatterns = useCallback(() => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternDisplayMode: 'selected',
            focusedChartPatternOccurrenceId: undefined
        }));
    }, []);

    const handleChartPatternKeyFilterChange = useCallback((nextChartPatternKeyFilter: ChartPatternKeyType | null) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternKeyFilter: nextChartPatternKeyFilter ?? undefined,
            chartPatternDisplayMode:
                prev.chartPatternDisplayMode === 'historicalOccurrences' || prev.chartPatternDisplayMode === 'pitReplay'
                    ? prev.chartPatternDisplayMode
                    : nextChartPatternKeyFilter
                      ? 'allCurrent'
                      : prev.chartPatternDisplayMode,
            focusedChartPatternOccurrenceId: undefined
        }));
    }, []);

    const handleShowAllCurrentChartPatterns = useCallback(() => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternDisplayMode: 'allCurrent',
            focusedChartPatternOccurrenceId: undefined
        }));
    }, []);

    const handleShowHistoricalChartPatterns = useCallback(() => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternDisplayMode: 'historicalOccurrences',
            focusedChartPatternOccurrenceId: undefined
        }));
    }, []);

    const handleShowPitReplayChartPatterns = useCallback(() => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            shouldIncludeChartPatterns: true,
            chartPatternDisplayMode: 'pitReplay',
            focusedChartPatternOccurrenceId: prev.focusedChartPatternOccurrenceId
        }));
    }, []);

    const handleDateRangeChange = useCallback((startDateTime?: string, endDateTime?: string) => {
        if (!startDateTime || !endDateTime) {
            console.error('Start and end date are required');
            return;
        }

        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            startDateTime,
            endDateTime,
            hasDateTimeChanged: true
        }));
    }, []);

    const handleDurationChange = useCallback((newDuration: BarDuration) => {
        setChartSettings((prev) => ({
            ...changeChartDuration(prev, newDuration),
            key: nanoid()
        }));
    }, []);

    const handleAddIndicator = useCallback((indicator: IndicatorInputWithChartSettings) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            indicatorsWithChartSettings: [...prev.indicatorsWithChartSettings, indicator]
        }));
    }, []);

    const handleRemoveIndicator = useCallback((indicatorId: string) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            indicatorsWithChartSettings: prev.indicatorsWithChartSettings.filter((indicator) => indicator.id !== indicatorId)
        }));
    }, []);

    const handleStrategyMarkersChange = useCallback(
        (markers: ChartMarker[], legend?: string) => {
            setChartSettings((prev) => ({
                ...prev,
                key: nanoid(),
                // Strategy entries are an optional overlay; clearing them does not
                // remove the caller's replay, execution, or outcome annotations.
                markers: [...(markersOverride ?? []), ...markers],
                legend: legend || ''
            }));
        },
        [markersOverride]
    );

    const handleComparisonChange = useCallback((compEntity: ComparisonEntity | null) => {
        setChartSettings((prev) => ({
            ...prev,
            key: nanoid(),
            comparisonEntity: compEntity
        }));
    }, []);

    // ── Focus Mode ──────────────────────────────────────────────────────

    const openFocusMode = useCallback(() => {
        const nextHeight = chartShellRef.current?.getBoundingClientRect().height;

        if (nextHeight) {
            setChartFlowHeight(Math.max(700, Math.round(nextHeight)));
        }

        setIsFocusMode(true);
    }, []);

    const closeFocusMode = useCallback(() => {
        setIsFocusSettingsOpen(false);
        setIsFocusMode(false);
    }, []);

    useEffect(() => {
        if (!canUseFocusMode) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isFocusMode && !isFocusSettingsOpen) {
                event.preventDefault();
                closeFocusMode();
                return;
            }

            if (event.key.toLowerCase() !== 'f' || isKeyboardShortcutTargetEditable(event.target)) {
                return;
            }

            event.preventDefault();

            if (isFocusMode) {
                closeFocusMode();
            } else {
                openFocusMode();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [canUseFocusMode, closeFocusMode, isFocusMode, isFocusSettingsOpen, openFocusMode]);

    // ── Derived Values ──────────────────────────────────────────────────

    const overlayToggles: OverlayToggle[] = [
        {
            key: 'candlestickPatterns',
            label: 'Candle Patterns',
            active: chartSettings.shouldIncludeCandlestickPatterns ?? false,
            onToggle: handleCandlestickPatternsToggle
        },
        {
            key: 'chartPatterns',
            label: 'Chart Patterns',
            active: chartSettings.shouldIncludeChartPatterns ?? false,
            onToggle: handleChartPatternsToggle,
            visible: entity.entityType === 'Asset' && isChartPatternDurationSupported(chartSettings.duration)
        },
        {
            key: 'trendlines',
            label: 'Trendlines',
            active: chartSettings.shouldIncludeTrendlines ?? false,
            onToggle: handleTrendlinesToggle
        },
        {
            key: 'highsLows',
            label: 'Highs/Lows',
            active: chartSettings.shouldIncludeHighsAndLows ?? false,
            onToggle: handleHighsAndLowsToggle
        },
        {
            key: 'microTrends',
            label: 'Micro Trends',
            active: chartSettings.shouldIncludeMicroTrends ?? false,
            onToggle: handleMicroTrendsToggle
        },
        {
            key: 'microtrendComparison',
            label: 'Microtrend comparison',
            active: microtrendComparison,
            onToggle: () => setMicrotrendComparison((value) => !value)
        },
        {
            key: 'supplyDemand',
            label: 'S/D Zones',
            active: chartSettings.shouldIncludeSupplyDemandZones ?? false,
            onToggle: handleSupplyDemandZonesToggle
        }
    ];

    const chartPatternPanelProps = {
        chartPatternDisplayMode: chartSettings.chartPatternDisplayMode ?? 'selected',
        chartPatternKeyFilter: chartSettings.chartPatternKeyFilter ?? null,
        duration: chartSettings.duration,
        focusedChartPatternOccurrenceId: chartSettings.focusedChartPatternOccurrenceId,
        historicalEvents: chartPatternHistoricalEvents,
        historicalOccurrences: chartPatternHistoricalOccurrences,
        isLoading: isChartPatternPanelLoading,
        summary: chartPatternSummary,
        onChartPatternKeyFilterChange: handleChartPatternKeyFilterChange,
        onFocusOccurrence: handleFocusChartPatternOccurrence,
        onShowHistoricalOccurrences: handleShowHistoricalChartPatterns,
        onShowPitReplay: handleShowPitReplayChartPatterns,
        onShowAllCurrent: handleShowAllCurrentChartPatterns,
        onShowSelected: handleShowSelectedChartPatterns
    };

    const indicatorCount = chartSettings.indicatorsWithChartSettings.length;

    const patternCount =
        chartPatternSummary?.snapshots.filter(
            (snapshot) =>
                (chartSettings.chartPatternKeyFilter ? snapshot.patternKey === chartSettings.chartPatternKeyFilter : true) &&
                snapshot.state !== 'failed' &&
                snapshot.state !== 'expired'
        ).length ?? 0;

    const settingsTabs: Tab[] = [
        {
            id: 'settings',
            label: 'Settings',
            content: (
                <ChartControls
                    chartSettings={chartSettings}
                    onAfterHoursToggle={handleAfterHoursToggle}
                    onAutoRefreshToggle={handleAutoRefreshToggle}
                    onDateRangeChange={handleDateRangeChange}
                />
            )
        },
        {
            id: 'indicators',
            label: indicatorCount > 0 ? `Indicators (${indicatorCount})` : 'Indicators',
            content: (
                <IndicatorSelector
                    activeIndicators={chartSettings.indicatorsWithChartSettings}
                    onAddIndicator={handleAddIndicator}
                    onRemoveIndicator={handleRemoveIndicator}
                />
            )
        },
        {
            id: 'strategy',
            label: 'Strategy',
            hidden: !isAuthenticated,
            content: (
                <StrategyEntriesSelector
                    duration={chartSettings.duration}
                    endDateTime={chartSettings.endDateTime}
                    entity={chartSettings.entity}
                    startDateTime={chartSettings.startDateTime}
                    onMarkersChange={handleStrategyMarkersChange}
                />
            )
        },
        {
            id: 'compare',
            label: chartSettings.comparisonEntity ? `Compare (${chartSettings.comparisonEntity.symbol})` : 'Compare',
            hidden: hideComparisonSearch,
            content: (
                <ComparisonSearch comparisonEntity={chartSettings.comparisonEntity ?? null} onComparisonChange={handleComparisonChange} />
            )
        },
        {
            id: 'patterns',
            label: patternCount > 0 ? `Patterns (${patternCount})` : 'Patterns',
            hidden: !shouldShowChartPatternPanel || !chartSettings.shouldIncludeChartPatterns,
            content: <ChartPatternPanel {...chartPatternPanelProps} />
        }
    ];

    const chartViewportStyle = isFocusMode ? { height: 'calc(100vh - 160px)' } : undefined;

    // ── Render ───────────────────────────────────────────────────────────

    return (
        <div
            className="chart-container relative w-full min-w-0"
            data-chart-loading-state={chartLoadingState}
            ref={chartShellRef}
            style={isFocusMode && chartFlowHeight ? { minHeight: `${chartFlowHeight}px` } : undefined}
        >
            <div className={isFocusMode ? 'fixed inset-0 z-40 bg-[#090909] p-3 md:p-4' : ''}>
                <div className={isFocusMode ? 'relative flex h-full flex-col gap-3' : 'relative'}>
                    {/* Loading overlay */}
                    {isLoading && !microtrendComparison && (
                        <div
                            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black bg-opacity-70"
                            data-chart-loading-overlay="true"
                        >
                            <div className="relative h-16 w-16">
                                <div className="absolute h-16 w-16 animate-spin rounded-full border-4 border-[rgba(30,172,255,0.1)] border-t-[#1eacff]" />
                            </div>
                            <div className="mt-4 font-bold text-[#1eacff]">Loading...</div>
                        </div>
                    )}

                    {/* Error alerts */}
                    {error && !microtrendComparison && <Alert variant="danger">{error.message}</Alert>}
                    {comparisonError && !microtrendComparison && <Alert variant="danger">{comparisonError.message}</Alert>}

                    {/* Toolbar above chart (normal mode) */}
                    {!isFocusMode && !hideControls && (
                        <ChartToolbar
                            actions={
                                canUseFocusMode ? (
                                    <Button size="sm" variant="outline-dark" onClick={openFocusMode}>
                                        Focus (F)
                                    </Button>
                                ) : undefined
                            }
                            duration={chartSettings.duration}
                            overlayToggles={overlayToggles}
                            onDurationChange={handleDurationChange}
                        />
                    )}

                    {/* Chart viewport area */}
                    <div className={isFocusMode ? 'flex-1 min-h-0 overflow-y-auto pb-2 pr-1' : ''}>
                        {microtrendComparison && (
                            <MicrotrendComparisonPanel
                                key={[
                                    chartSettings.entity.entityType,
                                    chartSettings.entity.id,
                                    chartSettings.option?.id,
                                    chartSettings.duration,
                                    chartSettings.startDateTime,
                                    chartSettings.endDateTime,
                                    chartSettings.shouldRemoveAfterHours
                                ].join(':')}
                                settings={chartSettings}
                                onUseRecentWindow={
                                    bars.length && !isLoading
                                        ? () => {
                                              const window = recentMicrotrendComparisonWindow(
                                                  bars,
                                                  chartSettings.entity.entityType,
                                                  chartSettings.duration
                                              );
                                              if (window) handleDateRangeChange(window.startDateTime, window.endDateTime);
                                          }
                                        : undefined
                                }
                            />
                        )}
                        <div
                            hidden={microtrendComparison}
                            className={isFocusMode ? 'rounded-2xl border border-zinc-800 bg-[#111111] p-3 shadow-2xl' : ''}
                        >
                            <div className="w-full min-w-0" ref={chartContainerRef} style={chartViewportStyle} />
                            <div
                                className={`w-full mt-2 p-1 bg-[#1a1a1a] text-white rounded min-h-[75px] ${
                                    chartSettings.consoleItems?.length ? '' : 'hidden'
                                }`}
                                id="outputConsole"
                            />
                            <div className="w-full flex flex-col mt-2" id="subChartContainer" />
                        </div>

                        {/* Settings panel below chart (normal mode) */}
                        {!isFocusMode && !hideControls && !hideSettingsPanel && (
                            <div className="mt-3">
                                <Tabs
                                    pills
                                    smallTabs
                                    contentClassName="mt-3"
                                    tabs={microtrendComparison ? settingsTabs.filter((tab) => tab.id === 'settings') : settingsTabs}
                                />
                            </div>
                        )}
                    </div>

                    {/* Toolbar at bottom (focus mode) */}
                    {isFocusMode && (
                        <ChartToolbar
                            actions={
                                <>
                                    <Button size="sm" variant="outline-dark" onClick={() => setIsFocusSettingsOpen(true)}>
                                        Options
                                    </Button>
                                    <Button size="sm" variant="dark" onClick={closeFocusMode}>
                                        Exit (Esc)
                                    </Button>
                                </>
                            }
                            className="rounded-xl border border-zinc-700 bg-[rgba(17,17,17,0.96)] px-3 shadow-2xl backdrop-blur"
                            duration={chartSettings.duration}
                            overlayToggles={overlayToggles}
                            onDurationChange={handleDurationChange}
                        />
                    )}
                </div>
            </div>

            {/* Focus mode settings modal */}
            <Modal
                className="max-h-[85vh] overflow-y-auto"
                draggable={false}
                isOpen={isFocusMode && isFocusSettingsOpen}
                size="xl"
                title="Chart Settings"
                onClose={() => setIsFocusSettingsOpen(false)}
            >
                <Tabs
                    pills
                    smallTabs
                    contentClassName="mt-3"
                    tabs={microtrendComparison ? settingsTabs.filter((tab) => tab.id === 'settings') : settingsTabs}
                />
            </Modal>
        </div>
    );
};

export default ChartContainer;
